import { createSlice } from "@reduxjs/toolkit";
import { IDetailService, IServices } from "Shared/Types/services-history";
import { RootState } from "../store";
import {
  addServiceHistory,
  getServicesFiles,
  getServicesId,
  getServicesList,
  saveComment,
  updateServiceHistoryPatch,
} from "./reducer";
import { IFile } from "Shared/Types/file";

interface ICompanyRequestsState {
  /** Params of the last table request, reused to reload after edits. */
  lastListParams: Parameters<typeof getServicesList>[0];
  error?: string | null;
  loading: boolean;
  isFuncLoading: boolean;
  success?: boolean | null;
  servicesList: IServices[];
  currentService: IDetailService | null;
  currentServicesFiles: IFile[];
  numPage: number;
}

const initialState: ICompanyRequestsState = {
  lastListParams: null,
  loading: false,
  isFuncLoading: false,
  error: null,
  servicesList: [],
  numPage: 0,
  currentService: null,
  currentServicesFiles: [],
};

const servicesSlice = createSlice({
  name: "sliceService",
  initialState,
  reducers: {
    clear(state) {
      state.error = null;
      state.loading = false;
      state.success = null;
      state.currentService = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getServicesList.pending, (state, action) => {
        if (action.meta.arg) state.lastListParams = action.meta.arg;
        state.loading = true;
      })
      .addCase(getServicesList.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getServicesList.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.servicesList = action.payload.results;
        state.numPage = action.payload.count;
      })
      .addCase(getServicesId.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.currentService = action.payload;
      });

    builder.addCase(getServicesFiles.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.success = true;
      state.currentServicesFiles = action.payload;
    });

    builder.addCase(addServiceHistory.pending, (state) => {
      state.isFuncLoading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(addServiceHistory.fulfilled, (state) => {
      state.isFuncLoading = false;
      state.error = null;
      state.success = true;
    });
    builder.addCase(addServiceHistory.rejected, (state, action) => {
      state.isFuncLoading = false;
      state.error = action.error.message;
    });
    builder.addCase(updateServiceHistoryPatch.pending, (state) => {
      state.isFuncLoading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(updateServiceHistoryPatch.fulfilled, (state) => {
      state.isFuncLoading = false;
      state.error = null;
      state.success = true;
    });
    builder.addCase(updateServiceHistoryPatch.rejected, (state, action) => {
      state.isFuncLoading = false;
      state.error = action.error.message;
    });
    builder.addCase(saveComment.pending, (state) => {
      state.isFuncLoading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(saveComment.fulfilled, (state) => {
      state.isFuncLoading = false;
      state.error = null;
      state.success = true;
    });
    builder.addCase(saveComment.rejected, (state, action) => {
      state.isFuncLoading = false;
      state.error = action.error.message;
    });
  },
});

export const { clear } = servicesSlice.actions;

export const selectServicesList = (state: RootState) =>
  state.services_history.servicesList;
export const selectServicesIsFuncLoading = (state: RootState) =>
  state.services_history.isFuncLoading;
export const selectDetailServices = (state: RootState) =>
  state.services_history.currentService;
export const selectServicesFiles = (state: RootState) =>
  state.services_history.currentServicesFiles;
export const selectServicesLoading = (state: RootState) =>
  state.services_history.loading;
export const selectServicesError = (state: RootState) =>
  state.services_history.error;
export const selectServicesSuccess = (state: RootState) =>
  state.services_history.success;
export const selectNumPage = (state: RootState): number =>
  state.services_history.numPage;
export const selectServicesListLastParams = (state: RootState) =>
  state.services_history.lastListParams;

export default servicesSlice;
